import { useState, useCallback, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { Box, CircularProgress, Container, Stack } from '@mui/material';
import axios from 'src/utils/axios';
// hooks
import useSettings from '../../../hooks/useSettings';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import Page from '../../../components/Page';
import { PageHeader } from '../../../sections/general/pages';
// sections
import PendingSignaturesTable from '../../../sections/employee/document/PendingSignaturesTable';
// utils
import { parseBizhavenError } from '../../../utils/parseError';
// types
import type { PendingSignatureItem } from '../../../sections/employee/document/PendingSignaturesTable';

// ----------------------------------------------------------------------

export default function DocsPendingPage() {
  const { themeStretch } = useSettings();
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [signatures, setSignatures] = useState<PendingSignatureItem[]>([]);

  const fetchPendingSignatures = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axios.get('/app/admin/pending-signatures');
      if (isMountedRef.current) {
        setSignatures(response.data.data);
      }
    } catch (err) {
      console.error(err);
      if (isMountedRef.current) {
        enqueueSnackbar(parseBizhavenError(err), { variant: 'error' });
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [isMountedRef, enqueueSnackbar]);

  // After signing, DocuSeal's webhook may take a moment to update our backend.
  // Poll a few times with increasing delays until the data reflects the change.
  const handleSigned = useCallback(
    (prevSignatureId?: number) => {
      const MAX_RETRIES = 5;
      const DELAYS = [1500, 2000, 3000, 4000, 5000];
      let attempt = 0;

      const poll = async () => {
        try {
          const response = await axios.get('/app/admin/pending-signatures');
          if (!isMountedRef.current) return;
          const newData: PendingSignatureItem[] = response.data.data;
          const stillPresent =
            prevSignatureId !== undefined
              ? newData.some(
                  (s) =>
                    s.id === prevSignatureId && s.status !== 'completed' && s.status !== 'declined'
                )
              : false;

          if (stillPresent && attempt < MAX_RETRIES - 1) {
            attempt += 1;
            setTimeout(poll, DELAYS[attempt]);
          } else {
            setSignatures(newData);
            setIsLoading(false);
          }
        } catch (err) {
          console.error(err);
          if (isMountedRef.current) {
            enqueueSnackbar(parseBizhavenError(err), { variant: 'error' });
            setIsLoading(false);
          }
        }
      };

      setIsLoading(true);
      setTimeout(poll, DELAYS[0]);
    },
    [isMountedRef, enqueueSnackbar]
  );

  useEffect(() => {
    fetchPendingSignatures();
  }, [fetchPendingSignatures]);

  return (
    <Page title="Pending Signatures">
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <Stack mb={5} direction="row" alignItems="center" justifyContent="space-between">
          <PageHeader
            title="Pending Signatures"
            subtitle="Documents awaiting your signature"
            caption="Review and sign documents that have been assigned to you."
          />
        </Stack>

        {isLoading ? (
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <PendingSignaturesTable
            signatures={signatures}
            onSigned={handleSigned}
            onClose={fetchPendingSignatures}
          />
        )}
      </Container>
    </Page>
  );
}
